import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

const PER_BOARD_PROPERTY = '_per_board';
const PREVIEW_PROPERTY = '_Engraving preview';
const LOGO_PROPERTY = 'Engraving logo';
const TEXT_PROPERTY = 'Engraving text';
const VIEW_LINK_PROPERTY = '_View engraving';
const STAFF_TEXT_PROPERTY = '_Engraving text';
const STAFF_LOGO_PROPERTY = '_Logo file';
const STAFF_PREVIEW_PROPERTY = '_Engraving preview';
const VIEW_PAGE_PATH = '/pages/engraving';
const UPLOADED_FILE = /\/uploads\/([a-f0-9]+\.[a-z0-9]+)$/i;
const MAX_LINK_PASSES = 20;
let reconciling = false;

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  return response.ok ? response.json() : null;
}

/** @param {unknown} url */
function uploadedFileName(url) {
  if (typeof url !== 'string') return '';
  return url.match(UPLOADED_FILE)?.[1] ?? '';
}

/**
 * Everything the staff needs, for the engraving service line: a link to a page that shows
 * the engraving, the text, the original logo file and the preview. Hidden from the customer.
 * Shopify serves customer uploads as downloads, so the page is what opens the preview.
 * @param {{ properties?: Record<string, string> | null }} board
 */
function staffProperties(board) {
  const properties = board.properties ?? {};
  const text = properties[TEXT_PROPERTY] ?? '';
  const preview = uploadedFileName(properties[PREVIEW_PROPERTY]);
  const logo = uploadedFileName(properties[LOGO_PROPERTY]);
  if (!preview && !logo) return null;

  const params = new URLSearchParams();
  if (preview) params.set('preview', preview);
  if (logo) params.set('logo', logo);
  if (text) params.set('text', text);

  /** @type {Record<string, string>} */
  const staff = { [VIEW_LINK_PROPERTY]: `${window.location.origin}${VIEW_PAGE_PATH}?${params}` };
  if (text) staff[STAFF_TEXT_PROPERTY] = text;
  if (logo) staff[STAFF_LOGO_PROPERTY] = properties[LOGO_PROPERTY];
  if (preview) staff[STAFF_PREVIEW_PROPERTY] = properties[PREVIEW_PROPERTY];
  return staff;
}

/**
 * Puts the staff details first on the first engraving service line that is missing them.
 * Returns true when a line changed, so the caller re-reads the cart (line keys change).
 */
async function addMissingEngravingLinks(cart) {
  const itemsByKey = new Map(cart.items.map((item) => [item.key, item]));

  for (const line of cart.items) {
    if (!(Number(line.properties?.[PER_BOARD_PROPERTY]) > 0)) continue;
    if (line.properties?.[VIEW_LINK_PROPERTY]) continue;

    const board = itemsByKey.get(line.parent_relationship?.parent_key);
    const staff = board && staffProperties(board);
    if (!staff) continue;

    const response = await fetch(`${Theme.routes.cart_change_url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.key, quantity: line.quantity, properties: { ...staff, ...line.properties } }),
    });
    return response.ok;
  }

  return false;
}

function refreshCartSections() {
  document.querySelectorAll('cart-items-component').forEach((cartItems) => {
    const sectionId = cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : '';
    if (!sectionId) return;
    sectionRenderer
      .renderSection(sectionId, { cache: false, mode: cartItems.isDrawer ? 'hydration' : 'full' })
      .catch(() => {});
  });
}

/**
 * Keeps each nested engraving service line at (board quantity × engravings per board),
 * so changing the board quantity in the cart keeps the engraving charge correct.
 * Also gives each service line the staff details of its engraving.
 */
async function reconcileEngravingLines() {
  if (reconciling) return;
  reconciling = true;

  try {
    let cart = await fetchCart();
    if (!cart) return;

    let linked = false;
    for (let pass = 0; pass < MAX_LINK_PASSES && (await addMissingEngravingLinks(cart)); pass++) {
      linked = true;
      cart = await fetchCart();
      if (!cart) return;
    }
    if (linked) refreshCartSections();

    const itemsByKey = new Map(cart.items.map((item) => [item.key, item]));

    const index = cart.items.findIndex((item) => {
      const perBoard = Number(item.properties?.[PER_BOARD_PROPERTY]);
      const parent = itemsByKey.get(item.parent_relationship?.parent_key);
      return perBoard > 0 && parent && item.quantity !== parent.quantity * perBoard;
    });
    if (index === -1) return;

    const line = cart.items[index];
    const parent = itemsByKey.get(line.parent_relationship.parent_key);
    const quantity = parent.quantity * Number(line.properties[PER_BOARD_PROPERTY]);

    const cartItems = document.querySelector('cart-items-component');
    if (cartItems && typeof cartItems.updateQuantity === 'function') {
      cartItems.updateQuantity({ line: index + 1, quantity, action: 'change' });
      return;
    }

    await fetch(`${Theme.routes.cart_change_url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.key, quantity }),
    });
  } catch (error) {
    console.warn('[engraving] Could not sync engraving lines', error);
  } finally {
    reconciling = false;
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const settled = event.promise ?? Promise.resolve();
  settled.then(() => setTimeout(reconcileEngravingLines, 0)).catch(() => {});
});

if (Theme.template?.name === 'cart') reconcileEngravingLines();
